import { Component, OnDestroy, Renderer2, ViewChild, inject, effect, input, output } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Subject, take, takeUntil } from "rxjs";
import { isDate, isValid } from "date-fns";
import { MatDatepicker } from "@angular/material/datepicker";

import { PayGroup } from "@shared/models/pay-groups";
import { MilestonesService } from "@shared/services/milestones/milestones.service";
import { PermissionsService } from "@shared/services/permissions/permissions.service";
import { ToastService } from "@shared/services/toast/toast.service";
import { EVENT_TIMES } from "@shared/constants/event-time-options";
import { PAY_DATES } from "@shared/constants/pay-date-options";
import { WEEKEND_OFFSETS } from "@shared/constants/weekend-offset-options";
import { Milestone, MilestoneUpdateDTO, PayCycle } from "@shared/models/pay-cycle.interface";
import {
	convertMilestoneDateToUTC,
	mergeDateAndTime,
	setFormatDate,
	convertTo24HourFormat
} from "@shared/utils/date.util";
import { QaLocatorService } from "@shared/utils/qa-locator.service";

@Component({
	selector: "app-payment-calendar-milestone-edition",
	templateUrl: "./payment-calendar-milestone-edition.component.html",
	styleUrls: ["./payment-calendar-milestone-edition.component.scss"]
})
export class PaymentCalendarMilestoneEditionComponent implements OnDestroy {
	@ViewChild("netBookingPicker") picker!: MatDatepicker<Date>;

	milestone = input<Milestone | undefined>();
	payCycle = input<PayCycle | undefined>();
	payGroup = input<PayGroup>();
	closed = output<{ reload: boolean }>();

	eventForm: FormGroup | undefined;
	exit = false;
	title = "Net payment";
	popupOpen = false; // By default it should be true, but for SPRINT 11 it will not be implemented so we set it to false.
	recurringSelected: "CURRENT" | "FOLLOWING" | "ALL" = "CURRENT";
	oldMilestone: Milestone | undefined;
	payDates: string[] = PAY_DATES;
	eventTimes: string[] = EVENT_TIMES;
	weekendOffets: string[] = WEEKEND_OFFSETS;
	canEdit = false;
	_milestone!: Milestone | undefined;
	milestoneDate!: Date;
	setDateTitle = "Set a date";
	initialEmptyDateValue = "";
	initialEmptyTimeValue = "00:00";
	netBookingDate?: Date;
	showBookingDate = false;

	private destroy$ = new Subject<void>();

	private formBuilder = inject(FormBuilder);
	private milestonesService = inject(MilestonesService);
	private toastService = inject(ToastService);
	private permissions = inject(PermissionsService);
	private renderer = inject(Renderer2);
	private qaLocatorService = inject(QaLocatorService);

	constructor() {
		effect(() => {
			const value = this.milestone();

			this._milestone = value;
			this.oldMilestone = value;
			this.initForm();
			this.showBookingDate = value?.type === "NETS" || value?.type === "PAYMENT";
		});
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	initForm(): void {
		this.permissions
			.canEditCalendarEvents()
			.pipe(take(1))
			.subscribe(res => {
				this.canEdit = res;
			});

		if (this._milestone && this._milestone.date) {
			const formattedDate: Date = setFormatDate(this._milestone!.date);
			const time: string = convertTo24HourFormat(formattedDate);

			this.eventForm = this.formBuilder.group({
				date: [{ value: formattedDate, disabled: !this.canEdit }, [Validators.required]],
				time: [{ value: time, disabled: !this.canEdit }, [Validators.required]]
			});

			this.milestoneDate = formattedDate;

			this.eventForm
				.get("date")
				?.valueChanges.pipe(takeUntil(this.destroy$))
				.subscribe(
					date => (this.milestoneDate = mergeDateAndTime(new Date(date), this.eventForm?.controls.time.value))
				);

			this.eventForm
				.get("time")
				?.valueChanges.pipe(takeUntil(this.destroy$))
				.subscribe(
					time => (this.milestoneDate = mergeDateAndTime(new Date(this.eventForm?.controls.date.value), time))
				);

			this.initNetBookingDateForm();
		}
	}

	hasBookingDate(milestone: Milestone): boolean {
		return !!milestone?.extraInfo?.netBookingDate;
	}

	initNetBookingDateForm(): void {
		this.setDateTitle = "Set Net Payment date";

		this.eventForm!.addControl(
			"netBookingDate",
			this.formBuilder.control({ value: this.initialEmptyDateValue, disabled: !this.canEdit })
		);

		this.eventForm!.addControl(
			"netBookingTime",
			this.formBuilder.control({ value: this.initialEmptyTimeValue, disabled: !this.canEdit })
		);

		if (this.hasBookingDate(this._milestone!)) {
			const formattedNetBookingDate: Date = setFormatDate(this._milestone?.extraInfo?.netBookingDate as Date);
			const netBookingTime: string = convertTo24HourFormat(formattedNetBookingDate);
			this.netBookingDate = formattedNetBookingDate;
			this.eventForm?.controls.netBookingDate.patchValue(formattedNetBookingDate);
			this.eventForm?.controls.netBookingTime.patchValue(netBookingTime);
		}

		this.eventForm!.get("netBookingDate")
			?.valueChanges.pipe(takeUntil(this.destroy$))
			.subscribe(
				netBookingDate =>
					(this.netBookingDate = mergeDateAndTime(
						new Date(netBookingDate),
						this.eventForm?.controls.netBookingTime.value
					))
			);

		this.eventForm!.get("netBookingTime")
			?.valueChanges.pipe(takeUntil(this.destroy$))
			.subscribe(
				netBookingTime =>
					(this.netBookingDate = mergeDateAndTime(
						new Date(this.eventForm?.controls.netBookingDate.value),
						netBookingTime
					))
			);
	}

	refactorDate(date: Date): string {
		const weekday: string = date.toLocaleDateString("US", { weekday: "long" });
		const month: string = date.toLocaleDateString("US", { month: "long" });
		const day: string = date.toLocaleDateString("US", { day: "numeric" });
		const year: string = date.toLocaleDateString("US", { year: "numeric" });

		return this.capitalize(weekday) + ", " + this.capitalize(month) + " " + day + ", " + year;
	}

	savePopup(recurring: "CURRENT" | "FOLLOWING" | "ALL") {
		this.recurringSelected = recurring;
		this.popupOpen = false;
	}

	capitalize(sentence: string): string {
		return sentence[0].toUpperCase() + sentence.substring(1).toLowerCase();
	}

	resetAll() {
		if (this.oldMilestone) {
			const formattedDate: Date = setFormatDate(this.oldMilestone.date);
			const time: string = convertTo24HourFormat(formattedDate);

			this.eventForm?.controls.date.patchValue(formattedDate);
			this.eventForm?.controls.time.patchValue(time);

			this.resetNetBookingDate();
		}
	}

	resetNetBookingDate() {
		if (this.hasBookingDate(this.oldMilestone!)) {
			const netBookingDate = this.oldMilestone?.extraInfo?.netBookingDate;
			if (netBookingDate) {
				const formattedNetBookingDate: Date = setFormatDate(netBookingDate);
				const formattedNetBookingTime: string = convertTo24HourFormat(formattedNetBookingDate);
				this.eventForm?.controls.netBookingDate.patchValue(formattedNetBookingDate);
				this.eventForm?.controls.netBookingTime.patchValue(formattedNetBookingTime);
			} else {
				this.eventForm?.controls.netBookingDate.patchValue(this.initialEmptyDateValue);
				this.eventForm?.controls.netBookingTime.patchValue(this.initialEmptyTimeValue);
			}
		} else {
			this.eventForm?.controls.netBookingDate.patchValue(this.initialEmptyDateValue);
			this.eventForm?.controls.netBookingTime.patchValue(this.initialEmptyTimeValue);
		}
	}

	cancel(reload: boolean) {
		this.exit = true;
		setTimeout(() => {
			this.closed.emit({ reload: reload });
		}, 655); // like in css
	}

	save() {
		if (this.payCycle()) {
			const milestoneDate = convertMilestoneDateToUTC(
				this.milestoneDate,
				this.payGroup()?.legalEntity?.data?.timeZone ?? "UTC"
			);

			const milestoneUpdateDTO: MilestoneUpdateDTO = {
				milestones: [
					{
						id: this._milestone!.id,
						version: this._milestone!.version,
						group: this._milestone!.group,
						type: this._milestone!.type,
						date: milestoneDate,
						params: []
					}
				]
			};

			if (isDate(this.netBookingDate) && isValid(this.netBookingDate)) {
				milestoneUpdateDTO.milestones[0].netBookingDate = convertMilestoneDateToUTC(
					this.netBookingDate!,
					this.payGroup()?.legalEntity?.data?.timeZone ?? "UTC"
				);
			}

			if (this._milestone?.status === "UNDEFINED") {
				milestoneUpdateDTO.milestones[0].status = "PENDING";
			} else if (this._milestone?.status === "HAS_ERRORS") {
				milestoneUpdateDTO.milestones[0].status = "ERROR";
			} else if (this._milestone?.status === "COMPLETE") {
				milestoneUpdateDTO.milestones[0].status = "COMPLETE";
			} else {
				milestoneUpdateDTO.milestones[0].status = this._milestone?.status;
			}

			this.milestonesService.updateMilestones(milestoneUpdateDTO).subscribe({
				next: () => {
					this.toastService.showSuccess("Milestone updated");
					this.cancel(true);
				},
				error: () => this.toastService.showError("Milestone update failed")
			});
		}
	}

	/**
	 * Initialize QA locators
	 * @param datePickerName: date picker name/used
	 */
	openDatePicker(datePickerName: string): void {
		setTimeout(() => {
			this.datePickerViewChange("month", datePickerName);
		});
	}

	/**
	 * Call function to render QA locators for date-picker
	 * @param view: date picker current view
	 * @param datePickerName: date picker name/used
	 */
	datePickerViewChange(view: string, datePickerName: string): void {
		this.qaLocatorService.datePickerViewChange(view, datePickerName, this.renderer);
	}
}
